import { CSSProperties } from 'react'

// Editor-set focal-point/zoom override for an image rendered with object-fit: cover
// inside an overflow: hidden frame. Mirrors the backend's ImageCrop
// (interfaces/ContentPages.ts): x/y are focal-point percentages (0-100, default
// 50/50), zoom a scale factor (0.5-3; above 1 magnifies the cover crop, below 1 the
// whole image letterboxes in via object-fit: contain). Null/absent = default crop.
export interface ImageCrop {
  x: number
  y: number
  zoom: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

// Inline styles for a cropped <img>. With object-position at x%/y% the content point
// at that fraction sits exactly at x%/y% of the element box — which is also where
// transform-origin anchors the scale — so the same stored crop holds at every card
// size and aspect ratio the hub renders. Values are clamped before hitting CSS since
// the crop is editor-supplied JSON.
export const imageCropStyles = (crop?: ImageCrop | null): CSSProperties => {
  if (!crop) return {}
  const x = clamp(Number(crop.x), 0, 100)
  const y = clamp(Number(crop.y), 0, 100)
  const zoom = clamp(Number(crop.zoom), 0.5, 3)
  if (!isFinite(x) || !isFinite(y) || !isFinite(zoom)) return {}
  const styles: CSSProperties = { objectPosition: `${x}% ${y}%` }
  if (zoom !== 1) {
    // Below 1 the fit switches to contain, so zooming out reveals the whole image
    // (letterboxed) instead of shrinking the cover crop. The focal-point invariant
    // (image's x% point sits at x% of the frame) holds for contain too.
    if (zoom < 1) styles.objectFit = 'contain'
    styles.transform = `scale(${zoom})`
    styles.transformOrigin = `${x}% ${y}%`
  }
  return styles
}
